import type { PayloadAction } from '@reduxjs/toolkit'
// eslint-disable-next-line no-duplicate-imports
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { initialEditCandidateData } from './CandidateListSliceConstants'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import candidateListApi from '../../../middleware/api/Recruitment/CandidateList/CandidateListApi'
import {
  CandidateListSliceState,
  CandidateListTableProps,
  GetAllTechnology,
  country,
  viewHandlerProps,
  AddNewCandidateDTO,
  UploadCandidateResumeDTO,
  CurrentAddCandidatePage,
  IsEditNewCandidateEmailExistsParams,
  IsEditNewCandidateMobileNumExistsParams,
  IncomingEditCandidateData,
  CandidateListsView,
  exportCandidateListProps,
  paginationIndexType,
  UploadResumeDTO,
  UploadResumeType,
  GetAllStatus,
} from '../../../types/Recruitment/CandidateList/CandidateListTypes'
import { initialCandidateFilterValues } from '../../../pages/Recruitment/CandidateList/CandidateListHelpers'

export const initialCandidateListState: CandidateListSliceState = {
  paginationData: {
    startIndex: 0,
    endIndex: 20,
  },
  isLoading: ApiLoadingState.idle,
  listSize: 0,
  allCountryDetails: {} as country,
  empCountries: [],
  allEmployeeDetailsList: [],
  getAllTechnology: [],
  allJobVacancies: { size: 0, list: [] },
  allCompaniesData: [],
  currentAddCandidatePage: CurrentAddCandidatePage.addCandidate,
  editCandidateData: initialEditCandidateData,
  candidateViewDetails: {} as CandidateListsView,
  recruitersDetails: [],
  allCandidateViewDetails: [],

  filterData: initialCandidateFilterValues,

  screenToggle: '',
  UploadResumeResponse: {} as UploadResumeType,
  getAllStatus: [],
}

const searchScheduledCandidate = createAsyncThunk(
  'candidateList/searchScheduledCandidate',
  async (props: CandidateListTableProps, thunkApi) => {
    try {
      return await candidateListApi.searchScheduledCandidate(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getEmpCountries = createAsyncThunk(
  'candidateList/getEmpCountries',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getEmpCountries()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getTechnology = createAsyncThunk(
  'candidateList/getTechnology',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getTechnology()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getStatus = createAsyncThunk(
  'candidateList/getAllStatus',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getStatus()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCountryWiseCandidatesList = createAsyncThunk(
  'candidateList/getCountryWiseCandidatesList',
  async (data: viewHandlerProps, thunkApi) => {
    try {
      return await candidateListApi.getCountryWiseCandidatesList(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteCandidate = createAsyncThunk(
  'candidateList/deleteCandidate',
  async (candidateId: number, thunkApi) => {
    try {
      return await candidateListApi.deleteCandidate(candidateId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllJobVacanciesThunk = createAsyncThunk(
  'candidateList/getAllJobVacanciesThunk',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getAllJobVacancies({
        startIndex: null,
        endIndex: null,
        searchJobTitle: null,
        status: null,
        vendorEmployeeId: null,
        isVendor: null,
      })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const checkCandidateEmailThunk = createAsyncThunk(
  'candidateList/checkCandidateEmailThunk',
  async (email: string, thunkApi) => {
    try {
      return await candidateListApi.checkCandidateEmail(email)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getAllEmployeeDetailsThunk = createAsyncThunk(
  'candidateList/getAllEmployeeDetailsThunk',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getAllEmployeeDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const checkCandidateMobileNumberThunk = createAsyncThunk(
  'candidateList/checkCandidateMobileNumberThunk',
  async (candidateMobileNumber: string, thunkApi) => {
    try {
      return await candidateListApi.checkCandidateMobileNumber(
        candidateMobileNumber,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getAllCompaniesDataThunk = createAsyncThunk(
  'candidateList/getAllCompaniesDataThunk',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getAllCompaniesData()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const addNewCandidateThunk = createAsyncThunk(
  'candidateList/addNewCandidate',
  async (finalData: AddNewCandidateDTO, thunkApi) => {
    try {
      return await candidateListApi.addNewCandidate(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const uploadCandidateResumeThunk = createAsyncThunk(
  'candidateList/uploadCandidateResumeThunk',
  async (data: UploadCandidateResumeDTO, thunkApi) => {
    try {
      return await candidateListApi.uploadCandidateResume(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const resumeCandidateListThunk = createAsyncThunk(
  'candidateList/resumeCandidateListThunk',
  async (data: UploadResumeDTO, thunkApi) => {
    try {
      return await candidateListApi.resumeCandidateList(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const addTechnologyThunk = createAsyncThunk(
  'candidateList/addTechnologyThunk',
  async (technology: string, thunkApi) => {
    try {
      return await candidateListApi.addTechnology(technology)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const deleteTechnologyThunk = createAsyncThunk(
  'candidateList/deleteTechnologyThunk',
  async (technologyId: number, thunkApi) => {
    try {
      return await candidateListApi.deleteTechnology(technologyId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const editCandidateDataThunk = createAsyncThunk(
  'candidateList/editCandidateDataThunk',
  async (candidateId: number, thunkApi) => {
    try {
      return await candidateListApi.editCandidateData(candidateId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const isEditNewCandidateEmailExistsThunk = createAsyncThunk(
  'candidateList/isEditNewCandidateEmailExistsThunk',
  async (finalParams: IsEditNewCandidateEmailExistsParams, thunkApi) => {
    try {
      return await candidateListApi.isEditNewCandidateEmailExists(finalParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const isEditNewCandidateMobileNumExistsThunk = createAsyncThunk(
  'candidateList/isEditNewCandidateMobileNumExistsThunk',
  async (finalParams: IsEditNewCandidateMobileNumExistsParams, thunkApi) => {
    try {
      return await candidateListApi.isEditNewCandidateMobileNumExists(
        finalParams,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const editNewCandidateThunk = createAsyncThunk(
  'candidateList/editNewCandidateThunk',
  async (finalData: IncomingEditCandidateData, thunkApi) => {
    try {
      return await candidateListApi.editNewCandidate(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const exportCandidateList = createAsyncThunk(
  'candidateList/exportCandidateList',
  async (props: exportCandidateListProps, thunkApi) => {
    try {
      return await candidateListApi.exportCandidateList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getRecruitersDetails = createAsyncThunk(
  'candidateList/getRecruitersDetails',
  async (_, thunkApi) => {
    try {
      return await candidateListApi.getRecruitersDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const candidateListSlice = createSlice({
  name: 'candidateList',
  initialState: initialCandidateListState,
  reducers: {
    setFilterData: (state, action) => {
      state.filterData = action.payload
    },
    setCurrentAddCandidatePage: (
      state,
      action: PayloadAction<CurrentAddCandidatePage>,
    ) => {
      state.currentAddCandidatePage = action.payload
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setPaginationData: (state, action) => {
      state.paginationData = action.payload
    },
    setScreenToggle: (state, action) => {
      state.screenToggle = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchScheduledCandidate.fulfilled, (state, action) => {
        state.allCandidateViewDetails = action.payload.list
        state.listSize = action.payload.size
      })
      .addCase(getAllJobVacanciesThunk.fulfilled, (state, action) => {
        state.allJobVacancies = action.payload
      })
      .addCase(getCountryWiseCandidatesList.fulfilled, (state, action) => {
        state.allCandidateViewDetails = action.payload.list
        state.listSize = action.payload.size
      })
      .addCase(getEmpCountries.fulfilled, (state, action) => {
        state.empCountries = action.payload
      })
      .addCase(getRecruitersDetails.fulfilled, (state, action) => {
        state.recruitersDetails = action.payload
      })
      .addCase(getTechnology.fulfilled, (state, action) => {
        state.getAllTechnology = action.payload
      })
      .addCase(getStatus.fulfilled, (state, action) => {
        state.getAllStatus = action.payload
      })
      .addCase(getAllEmployeeDetailsThunk.fulfilled, (state, action) => {
        state.allEmployeeDetailsList = action.payload
      })
      .addCase(getAllCompaniesDataThunk.fulfilled, (state, action) => {
        state.allCompaniesData = action.payload
      })
      .addCase(editCandidateDataThunk.fulfilled, (state, action) => {
        state.editCandidateData = action.payload
      })
      .addCase(resumeCandidateListThunk.fulfilled, (state, action) => {
        state.UploadResumeResponse = action.payload.data
      })
      .addMatcher(
        isAnyOf(
          searchScheduledCandidate.pending,
          getAllJobVacanciesThunk.pending,
          getCountryWiseCandidatesList.pending,
          getAllEmployeeDetailsThunk.pending,
          checkCandidateMobileNumberThunk.pending,
          getAllCompaniesDataThunk.pending,
          addNewCandidateThunk.pending,
          addTechnologyThunk.pending,
          deleteTechnologyThunk.pending,
          editCandidateDataThunk.pending,
          getTechnology.pending,
          getStatus.pending,
          isEditNewCandidateEmailExistsThunk.pending,
          isEditNewCandidateMobileNumExistsThunk.pending,
          editNewCandidateThunk.pending,
          getRecruitersDetails.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          searchScheduledCandidate.fulfilled,
          getAllJobVacanciesThunk.fulfilled,
          getCountryWiseCandidatesList.fulfilled,
          getEmpCountries.fulfilled,
          getTechnology.fulfilled,
          getStatus.fulfilled,
          getAllEmployeeDetailsThunk.fulfilled,
          checkCandidateMobileNumberThunk.fulfilled,
          getAllCompaniesDataThunk.fulfilled,
          addNewCandidateThunk.fulfilled,
          addTechnologyThunk.fulfilled,
          deleteTechnologyThunk.fulfilled,
          editCandidateDataThunk.fulfilled,
          isEditNewCandidateEmailExistsThunk.fulfilled,
          isEditNewCandidateMobileNumExistsThunk.fulfilled,
          editNewCandidateThunk.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          searchScheduledCandidate.rejected,
          getAllJobVacanciesThunk.rejected,
          getCountryWiseCandidatesList.rejected,
          getEmpCountries.rejected,
          getTechnology.rejected,
          getStatus.rejected,
          getAllEmployeeDetailsThunk.rejected,
          checkCandidateMobileNumberThunk.rejected,
          getAllCompaniesDataThunk.rejected,
          addNewCandidateThunk.rejected,
          addTechnologyThunk.rejected,
          deleteTechnologyThunk.rejected,
          editCandidateDataThunk.rejected,
          isEditNewCandidateEmailExistsThunk.rejected,
          isEditNewCandidateMobileNumExistsThunk.rejected,
          editNewCandidateThunk.rejected,
          getRecruitersDetails.rejected,
          resumeCandidateListThunk.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})
const isLoading = (state: RootState): LoadingState =>
  state?.candidateList.isLoading

const getAllCandidateDetails = (state: RootState): CandidateListsView[] =>
  state.candidateList.allCandidateViewDetails

const getAllCandidateViewDetails = (state: RootState): CandidateListsView[] =>
  state.candidateList.allCandidateViewDetails

const getAllEmpCountries = (state: RootState): country[] =>
  state.candidateList.empCountries

const listSize = (state: RootState): number => state.candidateList.listSize

const getAllTechnology = (state: RootState): GetAllTechnology[] =>
  state.candidateList.getAllTechnology
const getFilterData = (state: RootState): viewHandlerProps =>
  state.candidateList.filterData
const getPaginationData = (state: RootState): paginationIndexType =>
  state.candidateList.paginationData
const getScreenToggle = (state: RootState): string =>
  state.candidateList.screenToggle

const getUploadResumeResponse = (state: RootState): UploadResumeType =>
  state.candidateList.UploadResumeResponse

const getAllStatus = (state: RootState): GetAllStatus[] =>
  state.candidateList.getAllStatus

export const candidateListThunk = {
  searchScheduledCandidate,
  getEmpCountries,
  getTechnology,
  getCountryWiseCandidatesList,
  deleteCandidate,
  getAllJobVacanciesThunk,
  getAllEmployeeDetailsThunk,
  checkCandidateEmailThunk,
  checkCandidateMobileNumberThunk,
  getAllCompaniesDataThunk,
  addNewCandidateThunk,
  uploadCandidateResumeThunk,
  resumeCandidateListThunk,
  addTechnologyThunk,
  deleteTechnologyThunk,
  editCandidateDataThunk,
  isEditNewCandidateEmailExistsThunk,
  isEditNewCandidateMobileNumExistsThunk,
  editNewCandidateThunk,
  getRecruitersDetails,
  exportCandidateList,
  getStatus,
}

export const candidateListSelectors = {
  isLoading,
  listSize,
  getAllCandidateDetails,
  getAllTechnology,
  getAllEmpCountries,
  getAllCandidateViewDetails,
  getFilterData,
  getPaginationData,
  getScreenToggle,
  getUploadResumeResponse,
  getAllStatus,
}

export const candidateListService = {
  ...candidateListThunk,
  actions: candidateListSlice.actions,
  selectors: candidateListSelectors,
}

export default candidateListSlice.reducer
